<script lang="ts">
  /**
   * The Changes tab — who changed what, newest first.
   *
   * WHY THE OWNER SEES THIS AND NOT ONLY US. A community grant is total: anyone the
   * owner adds can turn a sequence on, rewrite the DM four hundred people are getting,
   * point a webhook somewhere new and pull members out of a queue. "Who did this" is a
   * question the owner has about their own team, and a log only Sideguide staff can read
   * does not answer it.
   *
   * READ-ONLY, because that is what the surface is: no route appends to this log — a row
   * is written by the mutation it describes, inside that mutation's own transaction.
   */
  import StateBlock from '@ui/StateBlock.svelte';
  import { timeAgo } from '@ui/member';
  import {
    fetchAdminActions, actionSentence, fieldLines, kindLabel,
    type AdminActionItem,
  } from '../adminActions';

  let { skoolId }: { skoolId: number } = $props();

  let loading = $state(true);
  let more = $state(false);
  let error = $state<string | null>(null);
  let items = $state<AdminActionItem[]>([]);
  let kinds = $state<string[]>([]);
  let cursor = $state<string | null>(null);
  // Which object kind the list is narrowed to, or null for everything. The cursor is
  // scoped to its filter on the server (a walk that changes filter halfway is a 400), so
  // changing this always starts a fresh walk rather than carrying the old cursor over.
  let kind = $state<string | null>(null);
  // Which row is open. One at a time: the before/after is a table, and several of them
  // expanded at once is a page nobody can scan.
  let open = $state<number | null>(null);

  // THE CURSOR IS A PARAMETER, NOT A READ. `$effect` subscribes to whatever it touched
  // synchronously, so a `load` that reached for `cursor` itself would make the effect
  // depend on a value `load` then writes — one page fetched, the cursor moved, the
  // effect re-armed, for ever. Passing it in keeps the effect's dependencies to the two
  // it actually watches.
  async function load(from: string | null, reset: boolean): Promise<void> {
    if (reset) { loading = true; } else { more = true; }
    try {
      const page = await fetchAdminActions(skoolId, { kind, cursor: from });
      items = reset ? page.items : [...items, ...page.items];
      cursor = page.next_cursor;
      // The server's own vocabulary, not a copy of it — a kind added on the server
      // appears here the day it is added.
      if (page.kinds?.length) kinds = page.kinds;
      error = null;
    } catch (e) {
      error = e instanceof Error ? e.message : 'Failed to load the change log.';
    } finally {
      loading = false;
      more = false;
    }
  }

  // Re-runs on a community switch, exactly like the sibling panels.
  $effect(() => { void skoolId; void kind; open = null; void load(null, true); });

  /** Which door the change came through. Only worth saying when it was not the
   *  dashboard — a paired Helper acting on somebody's behalf is the surprising case. */
  function doorLabel(auth: string | null | undefined): string {
    return auth === 'device' ? 'via the Helper' : '';
  }
</script>

<p class="text-sm text-base-content/60 -mt-2 mb-5 max-w-2xl">
  Everything anyone on your team changed here — sequences, webhooks and boards. Sideguide
  writes a line the moment the change is saved, and nothing can add to this list
  afterwards.
</p>

{#if kinds.length > 1}
  <div class="flex flex-wrap items-center gap-1.5 mb-4">
    <button class="badge badge-sm cursor-pointer {kind === null ? 'badge-primary' : 'badge-ghost'}"
            onclick={() => (kind = null)}>Everything</button>
    {#each kinds as k (k)}
      <button class="badge badge-sm cursor-pointer {kind === k ? 'badge-primary' : 'badge-ghost'}"
              onclick={() => (kind = k)}>{kindLabel(k)}</button>
    {/each}
  </div>
{/if}

{#if loading}
  <StateBlock state="loading" />
{:else if error}
  <StateBlock state="error" message={error} onretry={() => load(null, true)} />
{:else if items.length === 0}
  <StateBlock state="empty"
              message={kind ? 'Nothing has been changed here yet.' : 'No changes recorded yet.'} />
{:else}
  <ul class="flex flex-col divide-y divide-base-content/8 rounded-box bg-base-100
             ring-1 ring-base-content/10 px-4">
    {#each items as a (a.id)}
      {@const lines = fieldLines(a)}
      <li class="py-3">
        <div class="flex items-baseline gap-2 flex-wrap text-sm">
          <!-- The id is the fallback, not the answer: it is what the row stores, and it
               reads as nothing at all to the owner. A deleted account leaves its changes
               behind, which is the only way this branch is taken. -->
          <span class="redact font-medium">{a.user_name ?? `User ${a.user_id}`}</span>
          {#if a.is_staff}
            <!-- WHOSE HAND IT WAS. Sideguide staff reach a community by role, so a row
                 like this is a support visit rather than one of the owner's own team —
                 which is the difference between "who on my team did this" and "when did
                 you touch my account". The badge says so without naming anybody: the
                 server decides how much of the identity a given reader gets. -->
            <span class="badge badge-outline badge-xs">Sideguide</span>
          {/if}
          <span class="text-base-content/70">{actionSentence(a)}</span>
          {#if doorLabel(a.auth_kind)}
            <span class="badge badge-ghost badge-xs">{doorLabel(a.auth_kind)}</span>
          {/if}
          <span class="flex-1"></span>
          <span class="text-xs text-base-content/40 shrink-0">{timeAgo(a.occurred_at)}</span>
        </div>
        <div class="flex items-baseline gap-2 flex-wrap mt-0.5">
          {#if a.user_email}
            <span class="redact text-[0.7rem] text-base-content/40">{a.user_email}</span>
          {/if}
          {#if a.reason}
            <span class="redact text-[0.7rem] text-base-content/55">“{a.reason}”</span>
          {/if}
          {#if lines.length}
            <button class="text-[0.7rem] text-base-content/50 hover:text-base-content
                           underline underline-offset-2"
                    onclick={() => (open = open === a.id ? null : a.id)}>
              {open === a.id ? 'Hide what changed' : 'What changed'}
            </button>
          {/if}
        </div>

        {#if open === a.id && lines.length}
          <!-- THE REDUCED SHAPES, READ BACK. The server never keeps a webhook URL, a
               signing secret or a DM body in this log — a URL arrives as its host and an
               edited message as its length, its digest and its opening — so these rows
               are already safe. Marked anyway: an opening line of a DM is still the
               owner's words to one of their members. -->
          <div class="redact mt-2 overflow-x-auto rounded-box bg-base-200/40 p-2">
            <table class="table table-xs">
              <thead>
                <tr class="text-base-content/45">
                  <th></th><th>Before</th><th>After</th>
                </tr>
              </thead>
              <tbody>
                {#each lines as l (l.label)}
                  <tr class={l.changed ? '' : 'opacity-50'}>
                    <td class="align-top text-base-content/60 whitespace-nowrap">{l.label}</td>
                    <td class="align-top whitespace-pre-line
                               {l.changed ? 'line-through opacity-60' : ''}">{l.before}</td>
                    <td class="align-top whitespace-pre-line
                               {l.changed ? 'font-medium' : ''}">{l.after}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </li>
    {/each}
  </ul>

  {#if cursor}
    <div class="mt-4">
      <button class="btn btn-ghost btn-sm" disabled={more} onclick={() => load(cursor, false)}>
        {more ? 'Loading…' : 'Show older changes'}
      </button>
    </div>
  {/if}
{/if}
